import { useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../ConfirmationModal";

interface UnsavedChangesGuardProps {
  /**
   * Evaluated at navigation time (not render time), so callers can consult
   * refs — e.g. a "leave approved" ref flipped just before a post-save
   * redirect — without waiting for a re-render.
   */
  when: () => boolean;
}

/**
 * Blocks in-app navigation and tab close while a form has unsaved changes.
 * Mount it once per guarded form: in-app route changes show a confirm dialog
 * (stay / leave), and closing or reloading the tab triggers the browser's
 * native "leave site?" prompt. Requires the app's data router (useBlocker).
 */
const UnsavedChangesGuard = ({ when }: UnsavedChangesGuardProps) => {
  const { t } = useTranslation();
  const whenRef = useRef(when);
  whenRef.current = when;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      whenRef.current() &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  );

  // Native browser prompt for tab close / reload / external navigation.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (whenRef.current()) {
        e.preventDefault();
        // Required by some browsers to actually show the prompt.
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // If the form became clean while the dialog was up (e.g. a save completed),
  // let the pending navigation through without asking.
  useEffect(() => {
    if (blocker.state === "blocked" && !whenRef.current()) {
      blocker.proceed();
    }
  }, [blocker]);

  return (
    <ConfirmationModal
      isOpen={blocker.state === "blocked"}
      title={t("unsavedChanges.title")}
      message={t("unsavedChanges.message")}
      confirmText={t("unsavedChanges.leave")}
      cancelText={t("unsavedChanges.stay")}
      variant="warning"
      onConfirm={() => blocker.state === "blocked" && blocker.proceed()}
      onCancel={() => blocker.state === "blocked" && blocker.reset()}
    />
  );
};

export default UnsavedChangesGuard;
